import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useDebounce} from "use-debounce";
import {PaginatedResponseV2} from "@/types/PaginatedResponse";
import {SearchInput} from "@/components/search-input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {anoop, noop} from "@/lib/misc/noop";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";



type HasID = { id: string };

export type HeaderComponentProps = {
    label: string;
    columnIndex: number;
    onChange: (params: FilterQueryParams) => void;
    filterParams: FilterQueryParams;
}

type PaginatedSelectorParams<T> = {
    headers: string[],
    columnTemplate?: string,
    getColumns: (item: T) => (string|string[]|React.ReactNode)[],
    selectedIDs?: string[],
    setSelectedIDs?: (selectedIDs: string[]) => void,
    getItems: (search: string, page: number, filterParams?: FilterQueryParams) => Promise<PaginatedResponseV2<T> | null>
    disableSelect?: boolean
    hideSearch?: boolean
    hideSelectAll?: boolean
    onAdd?: (selectedID: string) => Promise<void>
    onRemove?: (selectedID: string) => Promise<void>
    renderActions?: (item: T) => React.ReactNode
    onColumnClick?: (item: T, columnIndex: number) => void
    nonClickableColumns?: number[]
    renderHeader?: (props: HeaderComponentProps) => React.ReactNode
    syncWithUrl?: boolean
    renderTopBarActions?: (search: string, filterParams: FilterQueryParams) => React.ReactNode
    renderBottomActions?: (search: string, filterParams: FilterQueryParams) => React.ReactNode
    isItemDisabled?: (item: T) => boolean
    disableSelectAllWhenEmpty?: boolean
    showSelectionCount?: boolean
    onRefetchReady?: (refetch: () => void) => void
    initialFilterParams?: FilterQueryParams
}

function diff(a: string[], b: string[]): string[] {
    const exclude = new Set(b);
    return a.filter(item => !exclude.has(item));
}


export function PaginatedSelector<T extends HasID>(
    {
        headers,
        columnTemplate,
        getItems,
        getColumns,
        selectedIDs = [],
        setSelectedIDs = noop,
        disableSelect = false,
        hideSearch = false,
        hideSelectAll = false,
        onAdd = anoop,
        onRemove = anoop,
        renderActions,
        onColumnClick,
        renderHeader,
        syncWithUrl = false,
        renderTopBarActions,
        renderBottomActions,
        isItemDisabled,
        disableSelectAllWhenEmpty = false,
        showSelectionCount = false,
        nonClickableColumns = [],
        onRefetchReady,
        initialFilterParams,
    }: PaginatedSelectorParams<T>
) {

    const searchInputRef = useRef<HTMLInputElement>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const [totalPages, setTotalPages] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [items, setItems] = useState<T[]>([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState<string>(() => syncWithUrl ? (searchParams.get("search") || "") : "");
    const [page, setPage] = useState<number>(() => {
        if (syncWithUrl) {
            const pageParam = searchParams.get("page");
            return pageParam ? parseInt(pageParam, 10) : 1;
        }
        return 1;
    });
    const [filterParams, setFilterParams] = useState<FilterQueryParams>(() => {
        if (syncWithUrl) {
            const filtersParam = searchParams.get("filters");
            if (filtersParam) {
                try {
                    return JSON.parse(filtersParam) as FilterQueryParams;
                } catch {
                    return initialFilterParams ?? {};
                }
            }
        }
        return initialFilterParams ?? {};
    });

    const [debouncedSearch] = useDebounce(search, 300)


    const searchKey = useMemo(() => `${page}-${debouncedSearch}-${JSON.stringify(filterParams)}`, [debouncedSearch, page, filterParams]);
    const prevSearchKey = useRef("")
    const prevGetItems = useRef(getItems)

    const [itemsLoading, setItemsLoading] = useState<Record<string, boolean>>({})

    const activeRequestRef = useRef(0)

    const runSearch = useCallback((search: string, page: number, filters: FilterQueryParams, background = false) => {
        const requestID = ++activeRequestRef.current
        let timer: ReturnType<typeof setTimeout> | null = null
        if (!background) {
            timer = setTimeout(() => {
                if (requestID === activeRequestRef.current) setLoading(true)
            }, 200)
        }
        getItems(search, page, filters).then((result) => {
            if (requestID !== activeRequestRef.current) return
            if (!result) return
            setItems(result.results)
            setTotalPages(result.totalPages ?? 0)
            setHasMore(result.hasMore ?? false)
        }).finally(() => {
            if (timer) clearTimeout(timer)
            if (requestID === activeRequestRef.current) setLoading(false)
        })
    }, [getItems])

    useEffect(() => {
        onRefetchReady?.(() => runSearch(debouncedSearch, page, filterParams, true))
    }, [runSearch, debouncedSearch, page, filterParams])

    useEffect(() => {
        // If getItems changed, reset the previous search key to force a re-fetch
        if (prevGetItems.current !== getItems) {
            prevGetItems.current = getItems
            prevSearchKey.current = ""
        }

        const newSearchKey = searchKey
        if (newSearchKey == prevSearchKey.current) return
        prevSearchKey.current = newSearchKey
        runSearch(debouncedSearch, page, filterParams)
    }, [debouncedSearch, page, filterParams, searchKey, runSearch, getItems]);

    const prevDebouncedSearch = useRef(debouncedSearch);
    useEffect(() => {
        if (debouncedSearch === prevDebouncedSearch.current) return;
        prevDebouncedSearch.current = debouncedSearch;
        setPage(1);
    }, [debouncedSearch])

    useEffect(() => {
        if(!searchInputRef.current) return
        searchInputRef.current.focus()
    }, []);

    // Sync state to URL
    useEffect(() => {
        if (!syncWithUrl) return;

        setSearchParams(prev => {
            const params = new URLSearchParams(prev);

            if (debouncedSearch) {
                params.set("search", debouncedSearch);
            } else {
                params.delete("search");
            }

            if (page !== 1) {
                params.set("page", page.toString());
            } else {
                params.delete("page");
            }

            if (Object.keys(filterParams).length > 0) {
                params.set("filters", JSON.stringify(filterParams));
            } else {
                params.delete("filters");
            }

            return params;
        }, { replace: true });
    }, [debouncedSearch, page, filterParams, syncWithUrl, setSearchParams]);


    const rows = useMemo(() => {
        return items.map(i => getColumns(i))
    }, [items])

    const gridClass = useMemo(() => {
        if (columnTemplate) return "grid flex-1";
        return `grid grid-cols-${headers.length} flex-1`;
    }, [columnTemplate, headers.length])

    const gridStyle = useMemo(() => {
        if (!columnTemplate) return undefined;
        return {gridTemplateColumns: columnTemplate};
    }, [columnTemplate])

    const hasActions = !!renderActions;


    const handleOnAdd = useCallback(async (id: string) => {
        setItemsLoading({...itemsLoading, selected: true})

        try {
            await onAdd(id)
        } catch(e) {

        }
        setItemsLoading({...itemsLoading, selected: false})

    }, [itemsLoading, onAdd])


    const handleOnRemove = useCallback(async (id: string) => {
        setItemsLoading({...itemsLoading, selected: true})

        try {
            await onRemove(id)
        } catch(e) {

        }
        setItemsLoading({...itemsLoading, selected: false})

    }, [itemsLoading, onAdd])


    const handleSelect = useCallback(async (id: string, selected: boolean) => {
        setItemsLoading({...itemsLoading, selected: true})


        try {
            if (selected) {
                setSelectedIDs([...selectedIDs, id]);
                await onAdd(id)
            } else {
                setSelectedIDs(selectedIDs.filter(i => i !== id));
                await onRemove(id)
            }
        } catch (e) {
            console.error(e)

            if (selected) {
                setSelectedIDs(selectedIDs.filter(i => i !== id));
            } else {
                setSelectedIDs([...selectedIDs, id]);
            }
        }

        setItemsLoading({...itemsLoading, selected: false})


    }, [itemsLoading])


    const selectableItems = useMemo(() => {
        return items.filter(item => !isItemDisabled?.(item))
    }, [items, isItemDisabled])

    const allSelected = useMemo(() => {
        if (selectableItems.length === 0) return false
        const res = selectableItems.filter(i => selectedIDs?.includes(i.id))
        return res.length === selectableItems.length

    }, [selectableItems, selectedIDs])

    const handleSelectAll = useCallback(async () => {


        setLoading(true)

        const ids = selectableItems.map(i => i.id)

        if (allSelected) {
            const newIDs = diff(selectedIDs, ids)
            setSelectedIDs(newIDs)
            ids.forEach(id => handleOnRemove(id))
        } else {
            const newIDs = [...selectedIDs, ...ids]
            console.log(newIDs)
            setSelectedIDs(Array.from(new Set(newIDs)))
            ids.forEach(id => handleOnAdd(id))
        }

        setLoading(false)


    }, [search, selectableItems, allSelected, selectedIDs, handleOnAdd, handleOnRemove])


    const selected = useCallback((id: string) => {
        return selectedIDs.includes(id);
    }, [selectedIDs]);


    const inputClassName = useMemo(() => {
        if(!loading) return "scale-150"
        return "scale-150 opacity-50"
    }, [loading])

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex gap-4 items-center">
                {!hideSearch && (
                    <div className="flex-1">
                        <SearchInput ref={searchInputRef} initialValue={search} onChange={setSearch}/>
                    </div>
                )}
                {renderTopBarActions && renderTopBarActions(debouncedSearch, filterParams)}
            </div>
            <div className="bg-white w-full">
                <div className="flex gap-4 p-3">
                    {!disableSelect && (
                        <div className="w-[30px]">
                            {!hideSelectAll && (
                                <input type="checkbox"
                                       className={inputClassName}
                                       onChange={handleSelectAll}
                                       checked={allSelected && !loading}
                                       disabled={loading || (disableSelectAllWhenEmpty && selectedIDs.length === 0)}
                                />
                            )}
                        </div>
                    )}


                    <div className={gridClass} style={gridStyle}>
                        {/* <div>*/}
                        {/*    <input type="checkbox" className="scale-150"/>*/}
                        {/* </div>*/}
                        {headers.map((header, i) => (
                            <div className="font-bold px-2" key={i}>
                                {renderHeader ? renderHeader({
                                    label: header,
                                    columnIndex: i,
                                    onChange: (newParams) => setFilterParams(prev => {
                                        // Merge new params with existing, removing keys with undefined values
                                        const merged = {...prev, ...newParams};
                                        Object.keys(merged).forEach(key => {
                                            if (merged[key] === undefined) {
                                                delete merged[key];
                                            }
                                        });
                                        return merged;
                                    }),
                                    filterParams: filterParams
                                }) : header}
                            </div>
                        ))}
                    </div>
                    {hasActions && <div className="w-[200px] font-bold">Actions</div>}
                </div>
                <hr className={"border-light-light-grey"}/>

                <div>
                    {loading && (
                        <div className="flex justify-center my-4">
                            <LoadingSpinner/>
                        </div>
                    )}
                    {!loading && rows.map((row, i) => (
                        <Row
                            key={items[i].id}
                            id={items[i].id}
                            selected={selected(items[i].id)}
                            handleSelect={handleSelect}
                            row={row}
                            disableSelect={disableSelect}
                            disabled={isItemDisabled?.(items[i]) ?? false}
                            loading={itemsLoading[items[i].id] ?? false}
                            actions={renderActions ? renderActions(items[i]) : undefined}
                            onColumnClick={onColumnClick ? (columnIndex) => onColumnClick(items[i], columnIndex) : undefined}
                            nonClickableColumns={nonClickableColumns}
                            gridStyle={gridStyle}
                        />
                    ))}
                    {!loading && rows.length == 0 && (
                        <div className={"text-center mt-8 mb-8"}>No results found</div>
                    )}
                </div>
                {!disableSelect && showSelectionCount && selectedIDs.length > 0 && (
                    <div className="px-4 pt-2 pb-1">
                        <div className="text-sm text-grey">
                            {selectedIDs.length} selected
                        </div>
                    </div>
                )}
                <div>
                    <Paginator currentPage={page} totalPages={totalPages} onChange={setPage} hasMore={hasMore}/>
                </div>
                {renderBottomActions && (
                    <div className="p-4 border-t border-light-light-grey">
                        {renderBottomActions(debouncedSearch, filterParams)}
                    </div>
                )}
            </div>
        </div>

    )

}

type RowProps = {
    id: string,
    row: (string|string[]|React.ReactNode)[],
    handleSelect?: (id: string, selected: boolean) => void,
    selected: boolean,
    disableSelect?: boolean,
    disabled?: boolean,
    loading?: boolean,
    actions?: React.ReactNode,
    onColumnClick?: (columnIndex: number) => void
    nonClickableColumns?: number[]
    gridStyle?: React.CSSProperties,
}

function Row(
    {
        id,
        row,
        handleSelect = noop,
        selected,
        disableSelect,
        disabled,
        loading,
        actions,
        onColumnClick,
        nonClickableColumns = [],
        gridStyle,
    }: RowProps,
) {

    const onSelect = useCallback((e: React.MouseEvent) => {
        if (disableSelect || disabled) {
            return
        }
        // Don't trigger selection if clicking on actions or clickable columns
        if ((e.target as HTMLElement).closest(".action-buttons") || (e.target as HTMLElement).closest(".clickable-column")) {
            return;
        }
        handleSelect(id, !selected);
    }, [selected, handleSelect, id, disableSelect, disabled]);

    const handleColumnClick = useCallback((columnIndex: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (onColumnClick) {
            onColumnClick(columnIndex);
        }
    }, [onColumnClick]);

    const gridClass = useMemo(() => {
        if (gridStyle) return "grid flex-1";
        return `grid grid-cols-${row.length} flex-1`;
    }, [gridStyle, row.length])


    return (
        <div
            className={`flex items-center gap-4 border-b last:border-none border-light-light-grey p-3 ${!disableSelect && !disabled ? "cursor-pointer" : ""} ${disabled ? "opacity-50" : ""}`}
            onClick={onSelect}>
            {!disableSelect && (<div className="w-[30px] flex items-center">
                <input type="checkbox"
                       className="scale-150"
                       checked={selected}
                       disabled={loading || disabled}
                />

            </div>)}
            <div className={`${gridClass}`} style={gridStyle}>
                {row.map((item, i) => (
                    <div key={i}
                         className={`px-2 flex items-center ${onColumnClick && !nonClickableColumns.includes(i) ? "clickable-column cursor-pointer hover:text-blue-600" : ""}`}
                         onClick={onColumnClick && !nonClickableColumns.includes(i) ? (e) => handleColumnClick(i, e) : undefined}>
                        {typeof item === "string" ? (
                            <div className="truncate">{item}</div>
                        ) : Array.isArray(item) ? (
                            <div>
                                {item.map(i => (
                                    <div key={i} className="truncate">{i}</div>
                                ))}
                            </div>
                        ) : (
                            item
                        )}
                    </div>
                ))}
            </div>
            {actions && (
                <div className="w-[200px] action-buttons flex gap-2 items-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <div className="flex-1">
                        {actions}
                    </div>
                </div>
            )}
        </div>

    )
}


type PaginatorProps = {
    currentPage: number;
    totalPages: number;
    onChange: (page: number) => void;
    hasMore?: boolean;
}

const Paginator: React.FC<PaginatorProps> = ({currentPage, totalPages, onChange, hasMore}) => {
    const handleFirst = useCallback(() => {
        if (currentPage !== 1) {
            onChange(1);
        }
    }, [currentPage, onChange]);

    const handlePrev = useCallback(() => {
        if (currentPage > 1) {
            onChange(currentPage - 1);
        }
    }, [currentPage, onChange]);

    const unknownTotal = !totalPages;

    const handleNext = useCallback(() => {
        if (unknownTotal ? hasMore : currentPage < totalPages) {
            onChange(currentPage + 1);
        }
    }, [currentPage, totalPages, hasMore, unknownTotal, onChange]);

    const handleLast = useCallback(() => {
        if (!unknownTotal && currentPage !== totalPages) {
            onChange(totalPages);
        }
    }, [currentPage, totalPages, unknownTotal, onChange]);

    return (
        <div className="flex items-center justify-center gap-2 py-4">
            {!unknownTotal && (
                <button
                    onClick={handleFirst}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    First
                </button>
            )}
            <button
                onClick={handlePrev}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Prev
            </button>
            <span className="px-3 py-1 text-sm font-medium text-gray-700">
                {unknownTotal ? `Page ${currentPage}` : `Page ${currentPage} of ${totalPages}`}
            </span>
            <button
                onClick={handleNext}
                disabled={unknownTotal ? !hasMore : currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Next
            </button>
            {!unknownTotal && (
                <button
                    onClick={handleLast}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Last
                </button>
            )}
        </div>
    );
};